/**
 * @fileoverview Fonte única de verdade do `accessScope` no frontend.
 *
 * Antes, a comparação `user?.accessScope === "internal"` estava repetida inline
 * em 6 arquivos. Centralizar importa porque alguns consumidores rodam ACIMA do
 * AuthProvider (ex.: QueuesContext, montado em App.js envolvendo o app inteiro)
 * e não têm o objeto `user` — daí o fallback lendo a claim do próprio JWT.
 *
 * ⚠️ Isto é UX, não segurança. Quem de fato bloqueia é o backend
 * (middleware/blockInternalOnly.ts). Esconder botão não protege API.
 */

import { canNow } from "../hooks/usePermission";

const decodeScopeFromToken = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    const raw = token.replace(/^"|"$/g, "");
    const base64 = raw.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded))?.accessScope;
  } catch {
    return undefined;
  }
};

/** Escopo efetivo: o objeto `user` quando já hidratado, senão a claim do JWT. */
export const getAccessScope = (user) =>
  user?.accessScope ?? decodeScopeFromToken();

/** "Apenas Interno (Chat, CRM)" — sem acesso ao módulo de atendimento. */
export const isInternalOnly = (user) => getAccessScope(user) === "internal";

/** "Apenas Atendimento" — sem acesso ao chat interno. */
export const isAttendanceOnly = (user) => getAccessScope(user) === "attendance";

/**
 * Página inicial por escopo. Usada no login, no redirect da raiz e sempre que
 * um usuário for mandado "pra casa" por não poder ver a rota pedida. Com a
 * regra nova de acesso ligada, as conversas só para quem tem tickets.view.
 * INVARIANTE: o destino nunca pode ser uma rota marcada com `requiresAttendance`
 * — senão o guard rebate e vira loop de redirect.
 */
export const getHomeRoute = (user) =>
  canNow("tickets.view", !isInternalOnly(user)) ? "/tickets" : "/internal-chat";
